import { styled } from "goober";
import { queries } from "../../shared/queries";
import { isRouteType } from "../../shared/stepConfig";
import { config } from "../environment";
import { useRecordListControlChange } from "../hooks/useRecordListControlChange";
import { useQuery } from "@rocicorp/zero/react";
import { useLinkPages } from "../hooks/useLinkPages";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { isSpaceMember } from "../hooks/useSpaces";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import { HelpTooltip } from "./HelpTooltip";
import { Toggle } from "./Toggle";
import { Backdrop } from "./Backdrop";
import { parseMultiFilter } from "./ListFilter";
import { MainScrollContainer } from "./MainScrollContainer";
import { type PageItem, PagesList } from "./PagesList";
import {
  HeaderActions,
  HeaderCountBadge,
  SearchField,
} from "./Settings/shared";
import { TabHeaderBar } from "./TabHeader";
import { Text } from "./Text";
import { workflowListFilters } from "./workflowListFilters";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Heading = styled("div")`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 24px;
`;

// Sentinel space-filter id for the bucket collapsing every space the viewer
// isn't a member of. Not a real space id, and not "all" (reserved by
// parseMultiFilter/toggleMultiFilter as the empty-selection marker).
const OTHER_SPACES_FILTER_ID = "__spaces_not_in__";

export function Links() {
  const [allConnectors] = useQuery(queries.connectorsIndex());
  const [allUsers] = useQuery(queries.allUsers());
  const displayName = useSpaceDisplayName();
  const [readableSpaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const memberSpaceIds = new Set(
    readableSpaces.filter(isSpaceMember).map((s) => s.id)
  );
  const recordControl = useRecordListControlChange();

  const {
    type: typeParam,
    sort,
    space: spaceParam,
    app: appParam,
    creator: creatorParam,
    otherSpaces,
    q,
  } = useCurrentUrl(routes.links);
  const typeFilter = isRouteType(typeParam) ? typeParam : "webpage";
  const showOtherSpaces = otherSpaces === "1";
  const spaceFilters = parseMultiFilter(spaceParam).filter(
    (id) => showOtherSpaces || memberSpaceIds.has(id)
  );
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);

  const memberSpaceOptions = readableSpaces
    .filter((s) => memberSpaceIds.has(s.id))
    .sort((a, b) => displayName(a).localeCompare(displayName(b)));
  const nonMemberSpaceIds = readableSpaces
    .filter((s) => !memberSpaceIds.has(s.id))
    .map((s) => s.id);
  const appOptions = [...allConnectors].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const creatorOptions = [...allUsers].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const search = new URLSearchParams();
  if (q?.trim()) search.set("q", q.trim());
  search.set("type", typeFilter);
  if (sort === "name") search.set("sort", "name");
  if (!showOtherSpaces) {
    search.set(
      "spaces",
      (spaceFilters.length ? spaceFilters : [...memberSpaceIds]).join(",")
    );
    search.set("shared", "false");
  } else if (spaceFilters.length > 0) {
    const otherSpaces = spaceFilters.includes(OTHER_SPACES_FILTER_ID);
    search.set(
      "spaces",
      [
        ...spaceFilters.filter((id) => id !== OTHER_SPACES_FILTER_ID),
        ...(otherSpaces ? nonMemberSpaceIds : []),
      ].join(",")
    );
    if (!otherSpaces) search.set("shared", "false");
  }
  if (appFilters.length > 0) search.set("apps", appFilters.join(","));
  if (creatorFilters.length > 0) {
    search.set("creators", creatorFilters.join(","));
  }
  const { items, total, hasMore, loading, loadMore } = useLinkPages(
    search.toString()
  );

  const visibleItems = showOtherSpaces
    ? items
    : items.filter(
        (item) => item.kind !== "shared" && memberSpaceIds.has(item.space.id)
      );
  const rows: PageItem[] = visibleItems.map((item) =>
    item.kind === "shared"
      ? { shared: item }
      : {
          workflow: {
            ...item,
            branches: [
              {
                id: item.branchId,
                name: "main",
                headCommit: { routes: item.routes },
              },
            ],
          },
          space: item.space,
          hideSpaceName: !memberSpaceIds.has(item.space.id),
        }
  );

  const filters = workflowListFilters({
    space: {
      options: memberSpaceOptions,
      values: spaceFilters,
      displayName,
      otherBucket: showOtherSpaces
        ? {
            id: OTHER_SPACES_FILTER_ID,
            label: "Spaces you’re not in",
          }
        : undefined,
    },
    app: { options: appOptions, values: appFilters },
    creator: { options: creatorOptions, values: creatorFilters },
  });

  return (
    <>
      <title>{`Links • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <Container>
          <TabHeaderBar>
            <Heading>
              <Text variant="text-regular-md">Links</Text>
              {total !== null && <HeaderCountBadge count={total} />}
            </Heading>
            <HeaderActions>
              <SearchField
                placeholder="Search links…"
                value={q ?? ""}
                onChange={(next) =>
                  navigate(null, { q: next }, { replace: true })
                }
                width={240}
              />
            </HeaderActions>
          </TabHeaderBar>
          <MainScrollContainer>
            <PagesList
              rows={rows}
              linksRoute={routes.links}
              showSpaceColumn
              routeFavorites
              filters={filters}
              headerActions={
                <HeaderActions style={{ marginLeft: 0, paddingRight: 8 }}>
                  <HeaderActions
                    style={{ marginLeft: 0, paddingRight: 0, gap: 4 }}
                  >
                    <Text
                      variant="text-small-md"
                      color="var(--text-secondary)"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Other spaces
                    </Text>
                    <HelpTooltip content="Includes links shared with everyone in your tenant, plus links your role lets you view, from spaces you haven’t joined." />
                  </HeaderActions>
                  <Toggle
                    isOn={showOtherSpaces}
                    ariaLabel="Include links from spaces you haven’t joined"
                    onToggle={() => {
                      recordControl(
                        "filter_other_spaces",
                        showOtherSpaces ? "off" : "on"
                      );
                      navigate(
                        null,
                        {
                          otherSpaces: showOtherSpaces ? "0" : "1",
                          space:
                            spaceFilters
                              .filter((id) => id !== OTHER_SPACES_FILTER_ID)
                              .join(",") || "all",
                        },
                        { replace: true }
                      );
                    }}
                  />
                </HeaderActions>
              }
              paging={{ hasMore, onLoadMore: loadMore }}
              isComplete={!loading && spacesQuery.type === "complete"}
              emptyTitle="No links yet"
              emptyDescription="Links you can access across your spaces will appear here."
            />
          </MainScrollContainer>
        </Container>
      </Backdrop>
    </>
  );
}
